const invert = require('lodash/invert');

const FREQ_OPTIONS = [
    { label: 'Daily', value: 'daily' },
    { label: 'Weekly', value: 'weekly' },
    { label: 'Semi-monthly', value: 'semiMonthly' },
    { label: 'Monthly', value: 'monthly' },
];

const FREQ_MAP = {
    'daily': 1,
    'weekly': 7,
    'semiMonthly': 14,
    'monthly': 30,
};

/**
 * @param {Array<{term:number, paymentFrequency:string}>} options
 * @param {number} term
 * @returns {*} available payment frequencies from the given array of options
 */
function getAvailablePaymentFrequencies(options, term) {
    return options
        .filter(option => option.term === term)
        .reduce((frequencies, option) => {
            if (!frequencies.includes(option.paymentFrequency)) {
                frequencies.push(
                    FREQ_OPTIONS.find(fo => fo.value === option.paymentFrequency)
                );
            }
            return frequencies;
        }, [])
        .sort((a, b) => FREQ_OPTIONS.findIndex(
            fo => fo.value === a) > FREQ_OPTIONS.findIndex(fo => fo.value === b
        ))
        .map((option) => option.value);
}

/**
 *
 * @param {string} paymentFrequency
 * @param {Array<string>} possibleFrequencies
 * @returns {*}
 */
function getNearestPaymentFrequency (paymentFrequency, possibleFrequencies = []) {

    const frequency = possibleFrequencies.find(freq => freq === paymentFrequency);
    if (frequency) {
        return frequency;
    }

    const frequencyValues = possibleFrequencies.map(freq => FREQ_MAP[freq] || 0);
    const goalValue = FREQ_MAP[paymentFrequency] || 0;

    const closestValue = frequencyValues.reduce(function(prev, curr) {
        return (Math.abs(curr - goalValue) < Math.abs(prev - goalValue) ? curr : prev);
    }, 0);

    const reverseFreqMap = invert(FREQ_MAP);

    if (reverseFreqMap[closestValue]) {
        return reverseFreqMap[closestValue];
    }

    return possibleFrequencies[0];
}

/**
 *
 * @param {boolean} pointsTradable
 * @param {number} buyRate
 * @param {number} points
 * @param {number} maxOriginationPercent
 * @param {number} originationPercent
 * @returns {number}
 */
function getFactorRate({pointsTradable, buyRate, points, maxOriginationPercent, originationPercent}) {
    let factor = buyRate + (points / 100);
    if (pointsTradable) {
        factor += ((maxOriginationPercent - originationPercent) / 100);
    }
    return toFixedNumber(factor, 3);
}

/**
 *
 * @param num
 * @param digits
 * @param base
 * @returns {number}
 */
function toFixedNumber(num, digits, base) {
    const pow = Math.pow(base || 10, digits);
    return Math.round(num * pow) / pow;
}

/**
 * Reduces an array of approval options down to a min and max boundary for a single parameter
 * Works for a parameter of the option, or an option's limits IE: option[param] || option.limits[param]
 * @param {Array<{limits}>} options
 * @param {string} param
 * @returns {*}
 */
function getParamBoundaries(options, param) {
    return options.reduce((res, option) => {
        const optionParam = option[param] || option.limits[param];
        const optionMinValue = typeof optionParam === 'object' ?
            optionParam.min :
            optionParam;
        const optionMaxValue = typeof optionParam === 'object' ?
            optionParam.max :
            optionParam;
        return {
            min: Math.min(optionMinValue, res.min),
            max: Math.max(optionMaxValue, res.max)
        };
    },{
        min: 9999999999999,
        max: 0
    });
}

/**
 *
 * @param {{originationAmount: {min: number, max: number}, originationPercent: {min: number, max: number}}} optionLimits Currently selected option limits
 * @param {number} amount Currently selected loan amount
 * @returns {{originationPercent: {absMin: number, absMax: number, min, max}, originationLimitsAreEqual: boolean, originationThreshhold: {min: (number|number), max: (number|number)}, originationAmount: {absMin: (number|number), absMax: (number|number), min: number, max: number}}}
 */
function getOriginationParams(optionLimits, amount) {
    // define min & max - explicit amounts take priority over percentages
    const optionOriginAmountLimits = optionLimits.originationAmount || {};
    const explicitMax = optionOriginAmountLimits.max;
    let explicitMin = optionOriginAmountLimits.min;
    const maxOriginationPercent = optionLimits.originationPercent.max;
    const minOriginationPercent = optionLimits.originationPercent.min;

    const calculatedMaxPercent = explicitMax ?
        (explicitMax / amount) * 100 :
        maxOriginationPercent;
    const calculatedMinPercent = explicitMin ?
        (explicitMin / amount) * 100 :
        minOriginationPercent;
    const originationLimitsAreEqual = minOriginationPercent === maxOriginationPercent;
    const minThreshhold = explicitMin ?
        explicitMin / (minOriginationPercent / 100) :
        0;
    const maxThreshhold = explicitMax ?
        explicitMax / (maxOriginationPercent / 100) :
        0;

    let absMaxOriginationPercent, absMinOriginationPercent;

    if(explicitMin && !explicitMax) {
        absMinOriginationPercent = Math.max(minOriginationPercent, calculatedMinPercent);
        absMaxOriginationPercent = Math.max(absMinOriginationPercent, maxOriginationPercent);
        explicitMin = toFixedNumber(amount * (absMinOriginationPercent / 100), 2, 10);
    }
    if(!explicitMin && explicitMax) {
        absMaxOriginationPercent = Math.min(maxOriginationPercent, calculatedMaxPercent);
        absMinOriginationPercent = Math.min(absMaxOriginationPercent, minOriginationPercent);
    }
    if(explicitMin && explicitMax) {
        absMinOriginationPercent = Math.max(calculatedMinPercent, minOriginationPercent);
        const ceiling = Math.min(calculatedMaxPercent, maxOriginationPercent);
        absMaxOriginationPercent = Math.max(ceiling, absMinOriginationPercent);
    }
    if(!explicitMin && !explicitMax) {
        absMinOriginationPercent = minOriginationPercent;
        absMaxOriginationPercent = maxOriginationPercent;
    }

    const minOriginationAmount = toFixedNumber(amount * (absMinOriginationPercent / 100), 2, 10);
    const maxOriginationAmount = toFixedNumber(amount * (absMaxOriginationPercent / 100), 2, 10);
    const maxBoundary = explicitMax || maxOriginationAmount;
    const absMinOriginationAmount = originationLimitsAreEqual ?
        Math.min(minOriginationAmount, maxBoundary) :
        explicitMin || minOriginationAmount;
    const absMaxOriginationAmount = originationLimitsAreEqual ?
        Math.min(maxOriginationAmount, maxBoundary) :
        explicitMax || maxOriginationAmount;
    absMinOriginationPercent = toFixedNumber(absMinOriginationPercent, 2, 10);
    absMaxOriginationPercent = toFixedNumber(absMaxOriginationPercent, 2, 10);

    return {
        originationAmount: {
            min: minOriginationAmount,
            absMin: absMinOriginationAmount,
            max: maxOriginationAmount,
            absMax: absMaxOriginationAmount
        },
        originationPercent: {
            min: minOriginationPercent,
            absMin: absMinOriginationPercent,
            max: maxOriginationPercent,
            absMax: absMaxOriginationPercent
        },
        originationThreshhold: {
            min: minThreshhold,
            max: maxThreshhold,
        },
        originationLimitsAreEqual
    }
}

/**
 *
 * @param {{limits: {amount: {min: number, max: number}, paymentAmountMax: number}, payments: number}} option Currently selected option limits
 * @param {string} calculationType
 * @param {number} factor
 * @param {string} roundingStrategy Which rounding strategy/calculation to use
 * @returns {number}
 */
function getOptionAvailableAmount({option, calculationType, factor, roundingStrategy}) {

    switch (calculationType) {
        case 'standard':
            return option.limits.amount.max;
        case 'maxPayment':
            const base = option.limits.paymentAmountMax * option.payments / factor;
            const calcNum = getValueByRoundingStrategy(roundingStrategy, base);
            return Math.min(
                option.limits.amount.max,
                calcNum
            );
        case 'loc':
            return option.limits.drawAmount.max;
        default:
            return option.limits.amount.max;
    }
}

/**
 *
 * @param roundingStrategy
 * @param base
 * @returns {number}
 */
function getValueByRoundingStrategy(roundingStrategy, base) {
    if (roundingStrategy === 'floorLowerHundred') {
        return floorLowerHundred(base);
    } else if (roundingStrategy === 'halfEvenClosestDollar') {
        return roundHalfEven(base);
    } else { // halfEven is the column default
        return roundHalfEven(base / 100) * 100;
    }
}

/**
 *
 * @param num
 * @returns {number}
 */
function roundHalfEven(num) {
    const decCheck = num - Math.floor(num);
    if (decCheck === .5) {
        return (2 * Math.round(num / 2));
    } else {
        return Math.round(num);
    }
}

/**
 *
 * @param num
 * @returns {number}
 */
function floorLowerHundred(num) {
    return Math.floor(num / 100) * 100;
}

/**
 *
 * @param factor
 * @param amount
 * @returns {number}
 */
function getPaybackAmount({ factor, amount }) {
    return toFixedNumber(factor * amount, 3);
}

/**
 *
 * @param payments
 * @returns {number}
 */
function getPaymentsNumber(payments) {
    return toFixedNumber(payments, 4);
}

/**
 *
 * @param payback
 * @param payments
 * @returns {number}
 */
function getPaymentAmount({ payback, payments }) {
    const base = (payback * 10000) / (payments * 10000);
    return toFixedNumber(base, 2);
}

/**
 *
 * @param amount
 * @param points
 * @returns {number}
 */
function getCommission({ amount, points }) {
    const base = amount * (points / 100);
    return toFixedNumber(base, 3);
}

/**
 *
 * @param num
 * @returns {number}
 */
function halfEvenClosestDollar(num) {
    return roundHalfEven(num);
}

module.exports = {
    getAvailablePaymentFrequencies,
    getCommission,
    getFactorRate,
    getNearestPaymentFrequency,
    getOptionAvailableAmount,
    getOriginationParams,
    getParamBoundaries,
    getPaybackAmount,
    getPaymentAmount,
    getPaymentsNumber,
    toFixedNumber
};
